import { ColumnDef } from '@tanstack/react-table';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import PratusTable from 'components/DesignSystems/PratusTable/PratusTable';
import { useUpsertIncidentStatusSummary } from 'components/IAP/hooks/incidentStatusSummaryHooks';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useMemo, useState } from 'react';
import { FormProvider, useForm, useFormState } from 'react-hook-form';
import { ResponderInjuriesFormFields } from 'types/iapBaseTypes';

const headerCell = (label: string) => (
  <span className="text-nowrap">{label}</span>
);

const ResponderInjuriesTable = ({
  statusSummaryId,
  originalResponderInjuries,
  readOnly = false,
}: {
  statusSummaryId: string;
  originalResponderInjuries: ResponderInjuriesFormFields;
  readOnly?: boolean;
}) => {
  const [rowBeingEdited, setRowBeingEdited] = useState<string | null>(null);
  const upsertIncidentStatusSummary =
    useUpsertIncidentStatusSummary(statusSummaryId);

  const form = useForm({
    mode: 'onChange',
    defaultValues: { responderInjuries: originalResponderInjuries },
    resetOptions: { keepDefaultValues: true },
  });

  const { register, getValues, reset } = form;

  const { defaultValues } = useFormState({ control: form.control });

  const isRowEditable = (rowId: string, rowBeingEditedId: string | null) => {
    return rowBeingEditedId === rowId;
  };

  const row = useMemo(() => {
    return {
      id: 'responderInjuriesRow',
      responderInjuriesThisReportingPeriod:
        defaultValues?.responderInjuries
          ?.responderInjuriesThisReportingPeriod ?? 0,
      responderInjuriesTotal:
        defaultValues?.responderInjuries?.responderInjuriesTotal ?? 0,
    };
  }, [defaultValues]);

  const columns = useMemo<
    ColumnDef<ResponderInjuriesFormFields & { id: string }>[]
  >(
    () => [
      {
        accessorKey: 'injuriesThisReportingPeriod',
        header: () =>
          headerCell('Total Responder Injuries This Reporting Period'),
        cell: ({ row }) => {
          if (isRowEditable(row.id, rowBeingEdited)) {
            return (
              <StylishNewInput
                className="form-control"
                type="number"
                placeholder="Enter responder injuries this reporting period"
                defaultValue={row.original.responderInjuriesThisReportingPeriod}
                {...register(
                  `responderInjuries.responderInjuriesThisReportingPeriod`
                )}
              />
            );
          } else {
            return (
              <span>{row.original.responderInjuriesThisReportingPeriod}</span>
            );
          }
        },
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },

      {
        accessorKey: 'injuriesTotal',
        header: () => headerCell('Total Responder Injuries To Date'),
        cell: ({ row }) => {
          if (isRowEditable(row.id, rowBeingEdited)) {
            return (
              <StylishNewInput
                className="form-control"
                type="number"
                placeholder="Enter responder injuries to date"
                defaultValue={row.original.responderInjuriesTotal}
                {...register(`responderInjuries.responderInjuriesTotal`)}
              />
            );
          } else {
            return <span>{row.original.responderInjuriesTotal}</span>;
          }
        },
        enableSorting: false,
        meta: { headerAlign: 'left' },
      },
      {
        id: 'actions',
        header: () => <span />,
        cell: ({ row }) => {
          if (rowBeingEdited === row.id) {
            return (
              <div className="d-flex justify-content-end gap-1">
                <StylishNewButton
                  className="button--secondary border-0"
                  type="submit"
                  onClick={(e) => {
                    e.stopPropagation();

                    const currentValues = getValues('responderInjuries');

                    if (!currentValues.responderInjuriesThisReportingPeriod) {
                      currentValues.responderInjuriesThisReportingPeriod = 0;
                    }

                    if (!currentValues.responderInjuriesTotal) {
                      currentValues.responderInjuriesTotal = 0;
                    }

                    upsertIncidentStatusSummary({
                      responderInjuriesThisReportingPeriod:
                        currentValues.responderInjuriesThisReportingPeriod,
                      responderInjuriesTotal:
                        currentValues.responderInjuriesTotal,
                    });
                    reset({
                      responderInjuries: {
                        responderInjuriesThisReportingPeriod:
                          currentValues.responderInjuriesThisReportingPeriod,
                        responderInjuriesTotal:
                          currentValues.responderInjuriesTotal,
                      },
                    });
                    setRowBeingEdited(null);
                  }}
                >
                  <SharedIcon iconName="check" classes="me-1" />
                </StylishNewButton>
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    reset({
                      responderInjuries: {
                        responderInjuriesThisReportingPeriod:
                          row.original.responderInjuriesThisReportingPeriod,
                        responderInjuriesTotal:
                          row.original.responderInjuriesTotal,
                      },
                    });
                    setRowBeingEdited(null);
                  }}
                >
                  <SharedIcon iconName="cancel" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          } else {
            if (readOnly) return null;
            return (
              <div className="d-flex justify-content-end gap-1">
                <StylishNewButton
                  className="button--secondary border-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setRowBeingEdited(row.id);
                  }}
                  disabled={rowBeingEdited !== null}
                >
                  <SharedIcon iconName="edit" classes="me-1" />
                </StylishNewButton>
              </div>
            );
          }
        },
        enableSorting: false,
        size: 160,
        meta: { headerAlign: 'left' },
      },
    ],
    [
      readOnly,
      register,
      rowBeingEdited,
      getValues,
      upsertIncidentStatusSummary,
      reset,
    ]
  );

  return (
    <>
      <FormProvider {...form}>
        <div className="d-flex flex-column justify-content-between flex-grow-1">
          <div className="d-flex justify-content-between align-items-end mb-2 flex-wrap gap-2">
            <div
              className="justify-content-between align-items-center d-flex flex-grow-1 gap-2"
              style={{ minWidth: 240 }}
            >
              <label style={{ whiteSpace: 'nowrap' }}>Responder Injuries</label>
            </div>
          </div>
          <PratusTable
            className="layout-auto pratus-table"
            columns={columns}
            data={[row]}
            getRowId={(row) => row.id}
          />
        </div>
      </FormProvider>
    </>
  );
};

export default ResponderInjuriesTable;
